import React, { useState } from 'react'
import {
  Button,
  Card,
  CircularProgress,
  Grid,
  Link,
  makeStyles,
  Typography,
  Paper
} from '@material-ui/core'
import { Form, Formik } from 'formik'
import CustomTextField from 'components/Form/CustomTextField'
import { SubjectInfoMessages, SPEAKER_INFO_TEXT, creationType } from 'constants/index'
import { Box, Divider } from '@mui/material'
import ProgressButton from 'components/ProgressButton'
import { useDispatch } from 'react-redux'
import { verifyGoogleRecaptcha } from 'store/auth'
import Recaptcha from 'components/Recaptcha'
import SubjectSpecificDetails from 'components/SubjectSpecificDetails'
import ImageSelector from 'components/ImageSelector'
import InfoIconAlongText from 'components/InfoIconAlongText'
import TipTapEditor from 'components/TipTapEditor/TipTapEditor'
import ErrorMessage from 'components/ErrorMessage'
import { colors } from 'utils/colors'
import { toast } from 'utils/notify'
import { speakerFormSchema } from 'schemas/speaker'
import { summarizeContent, SummarizationContentType } from 'utils/groq'
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined'
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import ConfirmModal from 'components/ConfirmationModal'

const Speaker = (props) => {
  const { speaker, handleSubmit, submitTitle } = props
  const dispatch = useDispatch()
  const classes = useStyles()
  const [loading, setLoading] = useState(false)
  const [recaptchaCode, setRecaptchaCode] = useState('')
  const [isRecaptchaValid, setIsRecaptchaValid] = useState(false)

  const [profileImage, setProfileImage] = useState(null)
  const [profileImageDeleted, setProfileImageDeleted] = useState(false)

  const [summarizing, setSummarizing] = useState(false)
  const [viewingOriginal, setViewingOriginal] = useState(false)
  const [confirmResummarize, setConfirmResummarize] = useState(false)

  const handleChangeRecaptcha = (value) => {
    setRecaptchaCode(value)
    if (value) {
      setIsRecaptchaValid(true)
    }
  }
  const handleExpired = () => {
    setIsRecaptchaValid(false)
  }

  const initialValues = {
    firstName: speaker?.firstName ?? '',
    lastName: speaker?.lastName ?? '',
    email: speaker?.email ?? '',
    company: speaker?.company ?? '',
    position: speaker?.position ?? '',
    bio: speaker?.bio ?? '',
    originalBio: speaker?.originalBio ?? '',
    websiteUrl: speaker?.websiteUrl ?? '',

    credential: speaker?.credential ?? '',
    phone: speaker?.phone ?? '',
    city: speaker?.city ?? '',
    state: speaker?.state ?? '',
    country: speaker?.country ?? ''
  }

  const handleLogoChange = (file) => {
    setProfileImage(file)
  }

  const handleLogoRemove = () => {
    setProfileImage(null)
    setProfileImageDeleted(true)
  }

  const handleSummarize = async (values, setFieldValue) => {
    setConfirmResummarize(false)
    setSummarizing(true)
    try {
      const originalBio = values.originalBio || values.bio
      const summary = await summarizeContent(originalBio, SummarizationContentType.SPEAKER_BIO)
      if (summary) {
        setFieldValue('originalBio', originalBio)
        setFieldValue('bio', summary)
        toast.success('Bio summarized.')
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to summarize bio.')
    } finally {
      setSummarizing(false)
    }
  }

  const handleRevertToOriginal = (values, setFieldValue) => {
    setFieldValue('bio', values.originalBio)
    setFieldValue('originalBio', '')
    setViewingOriginal(false)
  }

  return (
    <Card className={classes.mainCard} elevation={3}>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={() => speakerFormSchema(creationType.INVITATION)}
        onSubmit={async (values) => {
          try {
            setLoading(true)
            const isVerified = await dispatch(verifyGoogleRecaptcha(recaptchaCode))
            if (isVerified) {
              await handleSubmit({
                ...values,
                fullName: `${values.firstName} ${values.lastName}`,
                profileImage,
                profileImageDeleted
              })
            } else {
              toast.error('reCAPTCHA verification failed. Please try again.')
            }
          } catch (e) {
            // do nothing
          } finally {
            setLoading(false) // do nothing but stops the loading
          }
        }}>
        {({ values, setFieldValue, errors, touched }) => (
          <Form>
            <Box sx={{ pb: 1 }}>
              <Typography variant="h4" className={classes.pageTitle}>
                Register as a Speaker
              </Typography>
            </Box>

            <Paper className={classes.detailsPaper} elevation={1}>
              <SubjectSpecificDetails infoText={SubjectInfoMessages.SPEAKER} />
            </Paper>

            <Paper className={classes.formPaper} elevation={1}>
              <Typography variant="h6" className={classes.formSectionTitle}>
                Basic Information
              </Typography>
              <Divider className={classes.sectionDivider} />

              <Grid container spacing={3} className={classes.form}>
                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="Email"
                    name="email"
                    type="text"
                    disabled={!!speaker?.id}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <CustomTextField
                    label="First Name"
                    name="firstName"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <CustomTextField
                    label="Last Name"
                    name="lastName"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <CustomTextField
                    label="Credential"
                    name="credential"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <CustomTextField
                    label="Position/Title"
                    name="position"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>
                <Grid item xs={12} lg={4}>
                  <CustomTextField
                    label="Organization"
                    name="company"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="City"
                    name="city"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="State"
                    name="state"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="Country"
                    name="country"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <CustomTextField
                    label="Phone"
                    name="phone"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>

                <Grid item xs={12} lg={8}>
                  <CustomTextField
                    label="Website URL"
                    name="websiteUrl"
                    type="text"
                    className={classes.textField}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper className={classes.formPaper} elevation={1}>
              <Grid item xs={12}>
                <Box
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                  <label className={classes.labelA}>
                    <Typography variant="h6" className={classes.formSectionTitle}>
                      Speaker Bio
                    </Typography>
                    <InfoIconAlongText infoText={SPEAKER_INFO_TEXT.bio} />
                  </label>
                  {values.bio?.replace(/<[^>]*>/g, '').trim() && (
                    <Button
                      type="button"
                      size="small"
                      variant="outlined"
                      color="primary"
                      startIcon={
                        summarizing ? (
                          <CircularProgress size={14} color="inherit" />
                        ) : (
                          <AutoAwesomeOutlinedIcon />
                        )
                      }
                      disabled={summarizing}
                      onClick={() => {
                        if (values.originalBio) {
                          setConfirmResummarize(true)
                        } else {
                          handleSummarize(values, setFieldValue)
                        }
                      }}>
                      {summarizing
                        ? 'Summarizing...'
                        : values.originalBio
                        ? 'Re-summarize'
                        : 'Summarize with AI'}
                    </Button>
                  )}
                </Box>
                <Box className={classes.quillWrapper}>
                  <TipTapEditor
                    hasPlaceholders={false}
                    value={values.bio}
                    onChange={(value) => setFieldValue('bio', value)}
                    placeholder="Write bio here..."
                    className={classes.quillEditor}
                  />
                </Box>
                <ErrorMessage message={touched?.intro && errors?.intro} />
                {values.originalBio && (
                  <Box style={{ display: 'flex', gap: 16, marginTop: 4 }}>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      className={classes.linkButton}
                      disabled={summarizing}
                      onClick={() => setViewingOriginal((prev) => !prev)}>
                      <VisibilityOutlinedIcon fontSize="inherit" className={classes.linkIcon} />
                      {viewingOriginal ? 'Hide Original' : 'View Original'}
                    </Link>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      className={classes.linkButton}
                      disabled={summarizing}
                      onClick={() => handleRevertToOriginal(values, setFieldValue)}>
                      <RestartAltOutlinedIcon fontSize="inherit" className={classes.linkIcon} />
                      Undo AI
                    </Link>
                  </Box>
                )}
                {viewingOriginal && (
                  <Box className={classes.originalTextBox}>
                    <Box className={classes.originalTextLabel}>ORIGINAL — READ ONLY</Box>
                    <Box
                      className={classes.originalTextContent}
                      dangerouslySetInnerHTML={{ __html: values.originalBio }}
                    />
                  </Box>
                )}
                <ConfirmModal
                  dialogTitle="Summarize Bio"
                  dialogText="This bio has already been summarized. Do you want to summarize it again?"
                  open={confirmResummarize}
                  closeModal={() => setConfirmResummarize(false)}
                  handleConfirm={() => handleSummarize(values, setFieldValue)}
                />
              </Grid>
            </Paper>

            <Paper className={classes.formPaper} elevation={1}>
              <Grid item xs={12} md={6}>
                <Box className={classes.imageContainer}>
                  <ImageSelector
                    title="Profile Picture"
                    subTitle="Allowed Format: PNG/JPG/JPEG; Dimensions: 320x320"
                    infoText={SPEAKER_INFO_TEXT.profile}
                    onChange={handleLogoChange}
                    onRemove={handleLogoRemove}
                    initialImage={speaker?.profileImageUrl || null}
                    cropOnSelect={true}
                  />
                </Box>
              </Grid>
            </Paper>

            <Box className={classes.buttonBox}>
              <Recaptcha handleChange={handleChangeRecaptcha} onExpired={handleExpired} />
              <Box className={classes.actionButtons}>
                <ProgressButton
                  type="submit"
                  label={submitTitle}
                  className={classes.submitButton}
                  inProgress={loading}
                  disabled={loading || !isRecaptchaValid}
                />
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Card>
  )
}

export default Speaker

const useStyles = makeStyles({
  mainCard: {
    padding: '2rem',
    borderRadius: '12px',
    boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
    overflow: 'hidden',
    transition: 'all 0.3s ease-in-out'
  },
  pageTitle: {
    fontWeight: 600,
    color: colors.indigo,
    marginBottom: '0.5rem',
    fontSize: '1.75rem'
  },
  headBar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '2rem'
  },
  backButton: {
    borderRadius: '8px',
    textTransform: 'none',
    fontWeight: 500
  },
  submitButton: {
    borderRadius: '8px',
    boxShadow: '0 4px 10px rgba(21, 180, 219, 0.25)',
    textTransform: 'none',
    fontWeight: 500,
    padding: '0.5rem 1.5rem'
  },
  conferencePaper: {
    marginBottom: '2rem',
    borderRadius: '12px',
    overflow: 'hidden',
    border: `1px solid ${colors.lightIndigo}`
  },
  conferenceInfoBox: {
    backgroundColor: colors.lightIndigo,
    padding: '1.25rem',
    borderRadius: '8px'
  },
  conferenceValue: {
    fontWeight: 500
  },
  detailsPaper: {
    marginBottom: '2rem',
    borderRadius: '12px'
  },
  formPaper: {
    marginBottom: '2rem',
    borderRadius: '12px',
    padding: '1.5rem',
    transition: 'all 0.3s ease'
  },
  formSectionTitle: {
    color: colors.indigo,
    fontWeight: 500,
    fontSize: '1.25rem'
  },
  linkButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '13px',
    textTransform: 'none',
    cursor: 'pointer',
    background: 'none',
    border: 'none',
    padding: 0,
    '&:hover': {
      textDecoration: 'underline',
      background: 'none'
    },
    '&:disabled': {
      cursor: 'default',
      opacity: 0.5
    }
  },
  linkIcon: {
    fontSize: '15px'
  },
  originalTextBox: {
    marginTop: '0.5rem',
    padding: '12px',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
    backgroundColor: '#fafafa'
  },
  originalTextLabel: {
    fontSize: '11px',
    letterSpacing: '0.5px',
    color: '#9e9e9e',
    marginBottom: '6px'
  },
  originalTextContent: {
    fontSize: '14px',
    color: '#606060'
  },
  sectionDivider: {
    marginBottom: '1.5rem',
    backgroundColor: colors.lightIndigo,
    height: '2px'
  },
  labelA: {
    fontSize: '14px',
    letterSpacing: '0.3px',
    color: colors.indigo,
    display: 'flex',
    alignItems: 'center',
    gap: '0.4rem',
    fontWeight: 500,
    marginBottom: '0.5rem'
  },
  form: {
    marginTop: '1rem'
  },
  textField: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '8px',
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.indigo
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: colors.indigo,
        borderWidth: '2px'
      }
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: colors.indigo
    }
  },
  singleInfo: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'center'
  },
  label: {
    minWidth: '150px',
    fontSize: '15px',
    fontWeight: '600',
    color: colors.indigo
  },
  editorContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem'
  },
  imageContainer: {
    marginTop: '1rem'
  },
  buttonBox: {
    marginTop: '2rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '1rem'
  },
  actionButtons: {
    display: 'flex',
    gap: '1rem'
  },
  cancelButton: {
    borderRadius: '8px',
    textTransform: 'none',
    padding: '0.5rem 1.5rem',
    color: colors.grey
  }
})
